import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import SectionHeader from './ui/SectionHeader';

const Experience = () => {
  const [expandedId, setExpandedId] = useState(null);

  const experiences = [
    {
      id: 1,
      company: 'PT Pertamina Drilling Services Indonesia',
      role: 'HSE Officer',
      period: 'Mar 2026 - Sep 2026',
      duration: '7m',
      location: 'Onshore rig',
      current: false,
      responsibilities: [
        'Oversaw daily drilling on the 1,500 HP PDSI #09.2/N80UE onshore rig and checked compliance with company safety procedures and industry regulations',
        'Updated and applied HIRARC and Job Safety Analysis for critical drilling tasks',
        'Ran safety inductions, toolbox talks, and required safety training for rig personnel',
        'Managed the permit-to-work system, completed daily site inspections, and followed up on safety deviations',
        'Investigated incidents and near misses, recorded root causes, and set corrective actions',
      ],
    },
    {
      id: 2,
      company: 'Penta-Ocean Construction Co. Ltd.',
      role: 'Safety Supervisor',
      period: 'Feb 2024 - Nov 2025',
      duration: '1y 10m',
      current: false,
      responsibilities: [
        'Monitored the company safety management system on the project and checked that HSE standards were in place for workers and subcontractors',
        'Walked the work areas each day and logged observations in the Daily Observation Register',
        'Gave safety talks on site and ran HSE inductions for visitors and new workers',
      ],
    },
    {
      id: 3,
      company: 'PT Superintending Company of Indonesia (SUCOFINDO)',
      role: 'Supervisor HSE',
      period: 'Dec 2023 - Feb 2024',
      duration: '3m',
      current: false,
      responsibilities: [
        'Checked that HSE regulations, commitments, policies, procedures, guidelines, and visual management were applied on site',
        'Prepared and updated hazard identification, risk assessment, and risk control for the workplace',
        'Ran safety inductions for new supervisors on the internal project',
        'Checked emergency response equipment in the mess and office, and monitored PPE stock and condition',
        'Wrote the HSE report, investigated work accidents, and prepared accident reports',
        'Ran routine and non-routine safety patrols, and checked that each work area held an emergency response drill',
      ],
    },
    {
      id: 4,
      company: 'PT Timur Samudra Sejahtera',
      role: 'SHE-Q Coordinator',
      period: 'Sep 2022 - Nov 2023',
      duration: '1y 2m',
      current: false,
      responsibilities: [
        'Reviewed the monthly SHE program, reported it to the SHEQ Manager, and helped compile the yearly SHE program',
        'Supported compliance reviews, general risk assessments, and other safety assessments',
        'Checked SHE matters with the project team and subcontractors against the project specification, safety code, and related guidelines',
        'Reviewed the initial site emergency response plan and distributed copies to each work area',
        'Reviewed registers and permits for lifting equipment, lifting accessories, confined spaces, hot work, and lockout/tagout',
        'Kept SHE logs, helped check compliance with applicable laws and regulations, and advised project management on SHE issues',
        'Joined incident investigations, root cause analysis, and SHE statistics, and helped develop and present SHE training',
        'Promoted incident prevention for employees and visitors',
      ],
    },
    {
      id: 5,
      company: 'PT Timur Bahari',
      role: 'SHE Site Coordinator',
      period: 'May 2020 - Sep 2022',
      duration: '2y 4m',
      current: false,
      responsibilities: [
        'Monitored implementation of the company SHE management system on site',
        'Proposed the monthly and yearly SHE programs and reported progress each month',
        'Supported compliance reviews, risk assessments, and other safety assessments, and advised project management on SHE issues',
        'Prepared UKL-UPL documents and reported them to the client',
      ],
    },
    {
      id: 6,
      company: 'PT Timur Bahari',
      role: 'SHE Officer',
      period: 'Mar 2018 - May 2020',
      duration: '2y 2m',
      current: false,
      responsibilities: [
        'Monitored the company SHE management system and covered safety duties when the SHE Coordinator was away from site',
        'Helped keep registers and permits for lifting equipment, lifting accessories, confined spaces, hot work, and lockout/tagout',
      ],
    },
    {
      id: 7,
      company: 'PT Timur Bahari',
      role: 'Safety Supervisor',
      period: 'Sep 2017 - Mar 2018',
      duration: '6m',
      current: false,
      responsibilities: [
        'Applied SHE management system practices and checked that SHE standards were in place for workers',
        'Toured work areas daily and logged observations in the Daily Observation Register',
      ],
    },
  ];

  return (
    <section id="experience" className="section-rule bg-btc-void">
      <div className="container-max section-padding py-24">
        <SectionHeader
          number="03"
          label="Record"
          title="Career"
          kicker="Roles from September 2017 through September 2026, in drilling, port construction, and power plants."
        />

        <div className="surface-card overflow-hidden">
          <div className="hidden border-b border-white/10 md:grid md:grid-cols-12">
            <div className="label col-span-1 px-4 py-3">No</div>
            <div className="label col-span-4 px-4 py-3">Role</div>
            <div className="label col-span-4 px-4 py-3">Company</div>
            <div className="label col-span-2 px-4 py-3">Duration</div>
            <div className="col-span-1 px-4 py-3" />
          </div>

          {experiences.map((exp) => {
            const open = expandedId === exp.id;
            return (
              <article key={exp.id} className="border-b border-white/10 last:border-b-0">
                <button
                  type="button"
                  onClick={() => setExpandedId(open ? null : exp.id)}
                  className={`grid w-full min-h-11 grid-cols-12 items-center text-left transition-colors duration-200 ${
                    open ? 'bg-white/5' : 'hover:bg-white/5'
                  }`}
                  aria-expanded={open}
                >
                  <div className="col-span-2 px-4 py-5 font-mono text-xs text-btc-orange md:col-span-1">
                    {String(exp.id).padStart(2, '0')}
                  </div>
                  <div className="col-span-8 px-4 py-5 md:col-span-4">
                    <p className="font-heading font-semibold text-white">{exp.role}</p>
                    <p className="mt-1 text-xs text-btc-muted md:hidden">
                      {exp.company}
                    </p>
                    {exp.current && (
                      <span className="mt-2 inline-flex items-center gap-1 rounded-full border border-btc-orange/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-btc-orange md:hidden">
                        Now
                      </span>
                    )}
                  </div>
                  <div className="hidden px-4 py-5 text-btc-muted md:col-span-4 md:block">
                    <p>{exp.company}</p>
                  </div>
                  <div className="hidden px-4 py-5 font-mono text-sm text-white md:col-span-2 md:block">
                    {exp.duration}
                    {exp.current && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-btc-orange/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-btc-orange">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-btc-orange opacity-75" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-btc-orange" />
                        </span>
                        Now
                      </span>
                    )}
                  </div>
                  <div className="col-span-2 flex justify-end px-4 py-5 md:col-span-1">
                    <Plus
                      size={20}
                      strokeWidth={2}
                      className={`text-btc-orange transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
                    />
                  </div>
                </button>

                {open && (
                  <div className="border-t border-white/10 bg-btc-void p-6 md:p-8">
                    <div className="grid gap-6 sm:grid-cols-2">
                      <div>
                        <p className="label">Period</p>
                        <p className="mt-1 font-mono text-sm text-white">{exp.period}</p>
                      </div>
                      {exp.location && (
                        <div>
                          <p className="label">Location</p>
                          <p className="mt-1 font-mono text-sm text-white">{exp.location}</p>
                        </div>
                      )}
                    </div>
                    <ol className="mt-8 space-y-3">
                      {exp.responsibilities.map((resp, idx) => (
                        <li key={resp} className="flex gap-4 text-sm leading-relaxed text-btc-muted">
                          <span className="font-mono text-btc-orange">
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                          <span>{resp}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { label: 'Span', value: '9 years' },
            { label: 'Companies', value: '5' },
            { label: 'Positions', value: '7' },
            { label: 'Spec', value: 'HSE' },
          ].map((item) => (
            <div key={item.label} className="surface-card p-6">
              <p className="label">{item.label}</p>
              <p className="mt-2 font-heading text-lg font-semibold text-white">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
